import ApiService from './api.service';
import logger from '@/utils/logger';
import {
  countryIsoCodeToEnglishName,
  regionCodeToEnglishName,
} from '@/utils/addressFormatForCompanyApi';

const CompanyService = {
  async createCompany(formData) {
    try {
      // Validate required fields with specific messages
      const validationErrors = [];

      // Basic field validations
      if (!formData.companyName?.trim()) {
        validationErrors.push('Company name is required');
      }

      // Check for duplicate company name
      try {
        const companiesResponse = await ApiService.getAllCompanies();
        const companies = Array.isArray(companiesResponse.companies) ? companiesResponse.companies : [];
        const isDuplicate = companies.some(company =>
          company.name?.toLowerCase() === formData.companyName?.trim().toLowerCase()
        );
        if (isDuplicate) {
          validationErrors.push('A company with this name already exists. Please choose a different name.');
        }
      } catch (error) {
        logger.error('Error checking for duplicate company:', error);
      }

      if (!formData.legalName?.trim()) {
        validationErrors.push('Legal name is required');
      }
      if (!formData.email?.trim()) {
        validationErrors.push('Email is required');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.phone?.trim()) {
        validationErrors.push('Phone number is required');
      }
      if (!formData.addressLine1?.trim()) {
        validationErrors.push('Address is required');
      }
      if (!formData.city?.trim()) {
        validationErrors.push('City is required');
      }
      if (!formData.region?.trim()) {
        validationErrors.push('State/Region is required');
      }
      if (!formData.zipCode?.trim()) {
        validationErrors.push('ZIP code is required');
      }
      if (!formData.country?.trim()) {
        validationErrors.push('Country is required');
      }
      if (!formData.timezone?.trim()) {
        validationErrors.push('Timezone is required');
      }
      if (!formData.currencyId) {
        validationErrors.push('Currency is required');
      }

      // If there are validation errors, throw them
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join('\n'));
      }

      // Build payload to match backend requirements
      const companyData = {
        companyName: formData.companyName?.trim(),
        legalName: formData.legalName?.trim(),
        federalId: formData.federalId?.trim(),
        country: countryIsoCodeToEnglishName(formData.country?.trim()) || formData.country?.trim(),
        region: regionCodeToEnglishName(formData.country, formData.region?.trim()) || formData.region?.trim(),
        addressLine1: formData.addressLine1?.trim(),
        addressLine2: formData.addressLine2?.trim() || '',
        city: formData.city?.trim(),
        zipCode: formData.zipCode?.trim(),
        phone: formData.phone?.trim(),
        fax: formData.fax?.trim() || '',
        email: formData.email?.trim(),
        website: formData.website?.trim() || '',
        timezone: formData.timezone?.trim(),
        industryId: formData.industryId || 1,
        organizationType:
          formData.organizationType === '' || formData.organizationType === null || formData.organizationType === undefined
            ? undefined
            : Number(formData.organizationType),
        customerTerm: formData.customerTerm,
        vendorTerm: formData.vendorTerm,
        createQuotes: formData.createQuotes,
        primaryCurrency: formData.primaryCurrency,
        enableMultiCurrency: formData.enableMultiCurrency,
        fiscalYearStart: formData.fiscalYearStart,
        cashFlowTracking: formData.cashFlowTracking,
        offerType: formData.offerType,
        enableInventory: formData.enableInventory,
        multipleWarehouses: formData.multipleWarehouses,
        enableUnits: formData.enableUnits,
        inventoryScheme: formData.inventoryScheme,
        enableTax: formData.enableTax,
        taxItemType: formData.taxItemType,
        trackTaxPaid: formData.trackTaxPaid,
        costOfGoodsSold: formData.costOfGoodsSold,
        expenses: formData.expenses,
        otherIncome: formData.otherIncome,
        enableEncryption: formData.enableEncryption,
        encryptionPassword: formData.encryptionPassword,
        passwordHint: formData.passwordHint,
        currencyId: formData.currencyId
      };

      logger.log('Sending company data to server:', companyData);
      const response = await ApiService.createCompany(companyData);
      logger.log('Server response:', response.data);
      return response.data;
    } catch (error) {
      logger.error('Error in CompanyService.createCompany:', error);
      if (error.response?.data) {
        const errorData = error.response.data;
        if (Array.isArray(errorData.errors)) {
          throw new Error(errorData.errors.map(err => err.msg || err.message).join('\n'));
        }
        if (errorData.message) {
          throw new Error(errorData.message);
        }
        throw new Error(JSON.stringify(errorData));
      }
      if (error.message) {
        throw new Error(error.message);
      }
      throw new Error('An unknown error occurred while creating the company');
    }
  },
  async createCustomerGroup({ name, companyId }) {
    try {
      if (!name || !companyId) {
        throw new Error('Group name and companyId are required');
      }
      const response = await ApiService.createCustomerGroup({ name, companyId });
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },
  async getAllCustomerGroups(companyId) {
    try {
      if (!companyId) throw new Error('companyId is required');
      const response = await ApiService.get(`/company/customer-groups?companyId=${companyId}`);
      return response.data.groupList || [];
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },
  async deleteCustomerGroup(id) {
    try {
      if (!id) throw new Error('Group id is required');
      await ApiService.delete(`/company/customer-groups/${id}`);
      return true;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },
  async createVendorGroup({ name, companyId }) {
    try {
      if (!name || !companyId) throw new Error('Group name and companyId are required');
      const response = await ApiService.post('/company/vendor-groups', { name, companyId });
      return response.data.group;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },
  async getAllVendorGroups(companyId) {
    try {
      if (!companyId) throw new Error('companyId is required');
      const response = await ApiService.get(`/company/vendor-groups?companyId=${companyId}`);
      return response.data.groupList || [];
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },
  async deleteVendorGroup(id) {
    try {
      if (!id) throw new Error('Group id is required');
      await ApiService.delete(`/company/vendor-groups/${id}`);
      return true;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },
  async createInvoiceFrequency({ name, companyId }) {
    try {
      if (!name || !companyId) throw new Error('Frequency name and companyId are required');
      const response = await ApiService.post('/company/invoice-frequencies', { name, companyId });
      return response.data.frequency;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },
  async getAllInvoiceFrequencies(companyId) {
    try {
      if (!companyId) throw new Error('companyId is required');
      const response = await ApiService.get(`/company/invoice-frequencies?companyId=${companyId}`);
      return response.data.frequencyList || [];
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },
  async deleteInvoiceFrequency(id) {
    try {
      if (!id) throw new Error('Frequency id is required');
      await ApiService.delete(`/company/invoice-frequencies/${id}`);
      return true;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },
  async createTimesheetFrequency({ name, companyId }) {
    try {
      if (!name || !companyId) throw new Error('Frequency name and companyId are required');
      const response = await ApiService.post('/company/timesheet-frequencies', { name, companyId });
      return response.data.frequency;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },
  async getAllTimesheetFrequencies(companyId) {
    try {
      if (!companyId) throw new Error('companyId is required');
      const response = await ApiService.get(`/company/timesheet-frequencies?companyId=${companyId}`);
      return response.data.frequencyList || [];
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },
  /** Creates any built-in default frequencies that are not already present (requires auth). */
  async loadDefaultTimesheetFrequencies(companyId) {
    try {
      if (!companyId) throw new Error('companyId is required');
      const response = await ApiService.post(
        `/company/timesheet-frequencies/load-defaults?companyId=${companyId}`
      );
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },
  async deleteTimesheetFrequency(id) {
    try {
      if (!id) throw new Error('Frequency id is required');
      await ApiService.delete(`/company/timesheet-frequencies/${id}`);
      return true;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },
  /** Item categories. itemType: 'Product' | 'Service' | 'Inventory' (optional). */
  async createItemGroup({ name, companyId, itemType = null }) {
    try {
      if (!name || !companyId) throw new Error('Category name and companyId are required');
      const response = await ApiService.post('/company/item-groups', { name, companyId, itemType });
      return response.data.group;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },
  async getAllItemGroups(companyId, itemType = null) {
    try {
      if (!companyId) throw new Error('companyId is required');
      const typeQuery = itemType ? `&itemType=${encodeURIComponent(itemType)}` : '';
      const response = await ApiService.get(`/company/item-groups?companyId=${companyId}${typeQuery}`);
      return response.data.groupList || [];
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },
  async deleteItemGroup(id) {
    try {
      if (!id) throw new Error('Group id is required');
      await ApiService.delete(`/company/item-groups/${id}`);
      return true;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },
  async getAllSalesPersons(companyId) {
    try {
      if (!companyId) throw new Error('companyId is required');
      const salesPersons = await ApiService.getAllSalesPersons(companyId);
      return salesPersons || [];
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },
  async updateSalesPerson(salesPersonId, salesPersonData) {
    try {
      if (!salesPersonId) throw new Error('salesPersonId is required');
      const response = await ApiService.put(`/company/sales-persons/${salesPersonId}`, salesPersonData);
      return response.data;
    } catch (error) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }
};

export default CompanyService; 